import { ref, toRefs, reactive, computed } from 'vue'
import { Message } from 'view-design'
import { clone } from '@/common/js/just.js'
import { getCustomStorage, setCustomStorage } from '../utils.js'

/**
 * @description: 左侧菜单
 * @param {*} props
 * @return {*}
 */
export const useCustomTableMenu = (props) => {
  function handleMenuSelect(index) {
    const children = props.navs.map((nav) => nav.children).flat()
    const level = children.findIndex((child) => child.key === index)
    document.querySelectorAll('.indicator-section .section-title')[level].scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  return {
    handleMenuSelect
  }
}

/**
 * @description: 获取本地存储列
 * @param {*} props
 * @return {*}
 */
export const useCustomTableColLocalStorage = (props, tableCols) => {
  if (!props.name) return

  /**
   * @description: 获取本地存储列，与组件外部传入的默认列合并
   * @return {*}
   */
  function getTableCols(modifyCols) {
    const localCols = modifyCols || getCustomStorage(props.name)
    const rawData = []
    for (const i in props.rawData) {
      // 把菜单维度加到每个列数据上，后面用于分组
      const cols = props.rawData[i].map((col) => ({ ...col, dim: i }))
      rawData.push(...cols)
    }

    if (localCols) {
      const localTableCols = localCols.map((col) => {
        const [key, width] = col.split('|')
        // 获取缓存的列宽，获取不到的为0
        const colWidth = Number(width)
        return { key, width: Object.is(colWidth, NaN) ? 0 : colWidth }
      })

      const localKeys = localTableCols.map((l) => l.key)

      // 生成一个全部表格列长度的数组
      const newCols = []

      rawData.forEach((col, index) => {
        const colIndex = localTableCols.findIndex((localCol) => localCol.key === col.key)
        // 本地缓存存在的列
        if (colIndex > -1) {
          const localCol = localTableCols[colIndex]
          const colItem = { ...col, ...localCol, checked: true }

          // 有些没有缓存表格列宽的，使用外部配置的
          if (!colItem.width) colItem.width = col.width
          newCols.splice(index, 0, colItem)
        } else {
          // 新增或隐藏的列，默认不勾选
          const colItem = { ...col, checked: false }
          newCols.push(colItem)

          // 把本地缓存不存在的列，放置在原本位置，使localKeys数组长度 = rawColsData的数组长度，便于后面排序
          localKeys.splice(index, 0, colItem.key)
        }
      })
      newCols.sort((a, b) => localKeys.indexOf(a.key) - localKeys.indexOf(b.key))
      return newCols
    }
    return rawData
  }

  /**
   * @description: 拖动表头更新列宽外部组件ref调用
   * @param {*} newWidth
   * @param {*} column
   */
  function updateTableColWidth(colsArr, newWidth, column) {
    const newLocalCols = colsArr.map((col) => {
      if (col.includes(`${column.property}|`)) {
        return `${column.property}|${parseInt(newWidth)}`
      }
      return col
    })

    setCustomStorage(props.name, newLocalCols)
  }

  tableCols.value = getTableCols()
  return {
    getTableCols,
    updateTableColWidth
  }
}

/**
 * @description: 列勾选
 * @param {*} currentColItems 当前菜单下的列
 * @return {*}
 */
export const useCustomTableColSelect = (props, tableCols) => {
  const sortarr = ref([])
  const selectedList = computed(() => tableCols.value.filter((col) => col.checked))

  const sortableList = computed({
    get() {
      // 根据sortarr排序
      return selectedList.value.sort((a, b) => {
        if (sortarr.value.indexOf(a.key) === -1) {
          return 1
        }
        return sortarr.value.indexOf(a.key) - sortarr.value.indexOf(b.key)
      })
    },
    set(vals) {
      // vals 根据tableCols.value的顺序排序
      sortarr.value = vals.sort((a, b) => {
        return tableCols.value.indexOf(a.key) - tableCols.value.indexOf(b.key)
      }).map((col) => col.key)
    }
  })
  const selectedCount = computed(() => selectedList.value.length)
  const isCheckAll = {}
  for (const i in props.rawData) {
    isCheckAll[i] = false
  }
  const navCheckAll = ref(isCheckAll)

  function handleNavCheckAllChange(isCheckAll, dim) {
    tableCols.value = tableCols.value.map((col) => {
      // 禁用标记的的不能被取消勾选
      const isDisabled = col.disabled
      if (col.dim === dim) {
        return { ...col, checked: isCheckAll || !!isDisabled }
      }
      return col
    })
  }

  return {
    sortarr,
    navCheckAll,
    sortableList,
    selectedList,
    selectedCount,
    handleNavCheckAllChange
  }
}

/**
 * @description: 清空全部、删掉单个
 * @param {*} selectedList
 * @param {*} selectedColItems
 * @return {*}
 */
export const useCustomTableColRemove = (tableCols) => {
  function removeItem(item) {
    tableCols.value = tableCols.value.map((col) => {
      if (col.key === item.key) {
        return { ...col, checked: false }
      }
      return col
    })
  }

  function clearAll() {
    tableCols.value = tableCols.value.map((col) => {
      // 禁用标记的的不能被取消勾选
      const isDisabled = col.disabled
      return { ...col, checked: !!isDisabled }
    })
  }

  return {
    removeItem,
    clearAll
  }
}

/**
 * @description: 保存模板、确认选择
 * @param {*} selectedList
 * @param {*} handleClose
 * @param {*} emit
 * @return {*}
 */
export const useCustomTableColSave = (selectedList, emit) => {
  const saveModel = reactive({
    modifyState: false,
    modifyName: '',
    saveAsTemplate: false,
    templateName: ''
  })

  function confirmAction(formatedArr) {
    if (saveModel.saveAsTemplate && !saveModel.templateName) {
      Message.warning('请输入模板名称')
      return Promise.reject(new Error('请输入模板名称'))
    }

    if (saveModel.modifyState && !saveModel.modifyName) {
      Message.warning('请输入模板名称')
      return Promise.reject(new Error('请输入模板名称'))
    }

    if (!selectedList.value.length) {
      Message.warning('自定义列不能为空')
      return Promise.reject(new Error('自定义列不能为空'))
    }

    const columns = clone(selectedList.value)
    const params = {
      columns,
      formatedCols: formatedArr
      // template: saveAsTemplate.value ? { name: templateName.value } : null
    }
    if (saveModel.modifyState) {
      params.template = { modifyName: saveModel.modifyName }
    } else if (saveModel.saveAsTemplate) {
      params.template = { name: saveModel.templateName }
    }

    emit('change', params)

    return Promise.resolve({ columns })
  }

  return {
    ...toRefs(saveModel),
    confirmAction
  }
}
